/* ============================================================================
 * <vc-sticky-buy> — Vitaceuticals sticky add-to-cart bar
 *
 * Appears fixed at the bottom of the viewport when the buy zone scrolls out
 * of view. Hides again when the buy zone is visible. Submits the actual
 * product form when its ATC is clicked (no separate add-to-cart logic).
 *
 * Markup contract:
 *
 *   <vc-sticky-buy
 *     watch-selector="vc-supply-picker"      ← element to watch with IntersectionObserver
 *     hidden                                  ← starts hidden
 *   >
 *     <div data-vc-sticky-thumb>…</div>
 *     <div data-vc-sticky-info>
 *       <div data-vc-sticky-name>{{ product.title }}</div>
 *       <div data-vc-sticky-variant>{{ variant.title }}</div>
 *     </div>
 *     <div data-vc-sticky-price>$22.40</div>
 *     <button data-vc-sticky-atc>Add to cart</button>
 *   </vc-sticky-buy>
 *
 * Behaviour:
 *   • Listens for 'vc:variant-change' to update price + variant label
 *   • Clicks on [data-vc-sticky-atc] requestSubmit() the watched supply-picker's form
 *   • Toggles the [hidden] attribute based on intersection
 * ============================================================================ */

if (!customElements.get('vc-sticky-buy')) {
  customElements.define(
    'vc-sticky-buy',
    class VcStickyBuy extends HTMLElement {
      connectedCallback() {
        this._watchSelector = this.getAttribute('watch-selector') || 'vc-supply-picker';
        this._target = document.querySelector(this._watchSelector);
        if (!this._target) {
          // No buy zone on this page — keep hidden and bail.
          this.hidden = true;
          return;
        }

        // Init: hidden by default
        this.hidden = true;

        // Watch the buy zone; show sticky bar when it leaves the viewport.
        this._observer = new IntersectionObserver(
          entries => {
            for (const entry of entries) {
              this.hidden = entry.isIntersecting;
            }
          },
          { threshold: 0, rootMargin: '0px 0px -85% 0px' }
        );
        this._observer.observe(this._target);

        // Wire ATC delegate
        this._atc = this.querySelector('[data-vc-sticky-atc]');
        if (this._atc) {
          this._onAtc = e => {
            e.preventDefault();
            const form = this._target.querySelector('[data-vc-form]') || this._target.querySelector('form');
            if (!form) return;
            if (typeof form.requestSubmit === 'function') form.requestSubmit();
            else form.submit();
          };
          this._atc.addEventListener('click', this._onAtc);
        }

        // Listen for variant changes broadcast by vc-supply-picker
        this._onVariant = e => this._applyVariant(e.detail);
        document.addEventListener('vc:variant-change', this._onVariant);
      }

      disconnectedCallback() {
        if (this._observer) this._observer.disconnect();
        if (this._atc && this._onAtc) this._atc.removeEventListener('click', this._onAtc);
        if (this._onVariant) document.removeEventListener('vc:variant-change', this._onVariant);
      }

      _applyVariant(detail) {
        if (!detail) return;
        const priceEl   = this.querySelector('[data-vc-sticky-price]');
        const variantEl = this.querySelector('[data-vc-sticky-variant]');
        const thumbImg  = this.querySelector('[data-vc-sticky-thumb] img');

        if (priceEl && typeof detail.priceCents === 'number') {
          priceEl.textContent = this._formatMoney(detail.priceCents);
        }
        if (variantEl && detail.card) {
          // Pull a human label from a child of the active tier card if possible.
          const label = detail.card.querySelector('[data-vc-tier-label]') || detail.card.querySelector('.vc-tier-title');
          if (label) variantEl.textContent = label.textContent.trim();
        }
        if (thumbImg && detail.image) {
          thumbImg.src = detail.image;
        }
      }

      _formatMoney(cents) {
        // Best-effort client-side fallback. The server-rendered initial value
        // already has the store's correct money format; we only update on tier
        // change. Format using AUD locale since this is the Vitaceuticals AU store.
        const dollars = (cents / 100).toFixed(2);
        return '$' + dollars;
      }
    }
  );
}
